<template>
  <v-dialog v-model="addDialog" class="the-app-dialogs" persistent max-width="900px">
    <v-card>
      <form @submit.prevent="validateBeforeSubmit($event)">
        <v-layout row class="no-select" style="background-color:#09132f">
          <h3 id="dialog-title" class="text-uppercase px-3 pt-3" style="text-xs-center letter-spacing:2px">
            <v-layout align-center>
              <v-icon light x-large class="rgba(0,0,0,0.3)--text pb-1">fa-dolly</v-icon>
              <span class="px-3">{{ $t('Add Production') }}</span>
            </v-layout>
          </h3>
          <v-spacer />
          <v-card-actions>
            <v-btn fab icon dark @click="$emit('hidemodal')">
              <v-icon>close</v-icon>
            </v-btn>
          </v-card-actions>
        </v-layout>

        <v-card-title class="grey lighten-3">
          <v-layout column>
            <v-flex xs12 class="headline no-select pb-1">
              <v-layout justify-end />
            </v-flex>

            <v-layout row wrap>
              <v-flex xs11 :class="{ xs12: !hasPermissions(['users_store']) }" pa-2>
                <v-autocomplete
                  id="production-focus-me"
                  :items="fullStaffList"
                  v-model="form.staff_id_fk"
                  :label="`${$t('Staff')} *`"
                  box
                  multiple
                  chips
                  deletable-chips
                  hide-details
                  :error="errors.has('Staff')"
                  background-color="white"
                  v-validate="'required'"
                  data-vv-name="Staff"
                  :menu-props="{ transition: 'slide-y-transition' }"
                >
                  <template v-slot:selection="{ item, index }">
                    <v-chip v-if="index < 3">
                      <span>{{ item.text }}</span>
                    </v-chip>
                    <span v-if="index === 3" class="grey--text caption"
                      >(+{{ form.staff_id_fk.length - 3 }} others)</span
                    >
                  </template>
                </v-autocomplete>
              </v-flex>

              <v-flex xs1 pt-3 v-if="hasPermissions(['users_store'])">
                <v-tooltip top>
                  <v-btn label @click="$emit('add-staff')" slot="activator" icon fab small color="green">
                    <v-icon color="white" size="25">add</v-icon>
                  </v-btn>
                  <span>{{ $t('Add new staff') }}</span>
                </v-tooltip>
              </v-flex>

              <v-flex xs11 :class="{ xs12: !hasPermissions(['users_store']) }" pa-2>
                <v-autocomplete
                  :items="fullFactoriesList"
                  v-model="form.factory_id_fk"
                  :label="`${$t('Factory')} *`"
                  box
                  hide-details
                  :error="errors.has('factory')"
                  background-color="white"
                  v-validate="'required'"
                  data-vv-name="factory"
                  :menu-props="{ transition: 'slide-y-transition' }"
                />
              </v-flex>

              <v-flex xs1 pt-2 v-if="hasPermissions(['factory_store'])">
                <v-tooltip top>
                  <v-btn label @click="$emit('add-factory')" slot="activator" icon fab small color="green">
                    <v-icon color="white" size="25">add</v-icon>
                  </v-btn>
                  <span>{{ $t('Add new factory') }}</span>
                </v-tooltip>
              </v-flex>

              <v-flex pa-2 xs5 :class="{ xs6: !hasPermissions(['products_store']) }">
                <v-autocomplete
                  :items="fullProductList"
                  v-model="form.product_id_fk"
                  :label="`${$t('Product')} *`"
                  box
                  hide-details
                  :error="errors.has('Product')"
                  background-color="white"
                  v-validate="'required'"
                  data-vv-name="Product"
                  :menu-props="{ transition: 'slide-y-transition' }"
                />
              </v-flex>

              <v-flex xs1 pt-2 v-if="hasPermissions(['products_store'])">
                <v-tooltip top>
                  <v-btn label @click="$emit('add-product')" slot="activator" icon fab small color="green">
                    <v-icon color="white" size="25">add</v-icon>
                  </v-btn>
                  <span>{{ $t('Add new product') }}</span>
                </v-tooltip>
              </v-flex>

              <v-flex pa-2 xs5 :class="{ xs6: !hasPermissions(['mixtures_store']) }">
                <v-autocomplete
                  :items="fullMixtureList"
                  v-model="form.mixture_id_fk"
                  :label="`${$t('Mixture')} *`"
                  box
                  hide-details
                  :error="errors.has('Mixture')"
                  background-color="white"
                  v-validate="'required'"
                  data-vv-name="Mixture"
                  :menu-props="{ transition: 'slide-y-transition' }"
                  @input="getMixtureInfo(form.mixture_id_fk)"
                />
              </v-flex>

              <v-flex xs1 pt-2 v-if="hasPermissions(['mixtures_store'])">
                <v-tooltip top>
                  <v-btn label @click="$emit('add-mixture')" slot="activator" icon fab small color="green">
                    <v-icon color="white" size="25">add</v-icon>
                  </v-btn>
                  <span>{{ $t('Add new mixture') }}</span>
                </v-tooltip>
              </v-flex>

              <v-flex xs3 class="pa-2">
                <v-text-field
                  v-model="form.used_mixture_quantity"
                  :label="`${$t('Used Mixture Quantity')} *`"
                  box
                  hide-details
                  name="used_mixture_quantity"
                  background-color="white"
                  :error="errors.has('Used Mixture Quantity')"
                  v-validate="'required|decimal:3|min_value:0'"
                  data-vv-name="Used Mixture Quantity"
                />
              </v-flex>

              <v-flex xs3 class="pa-2">
                <v-text-field
                  v-model="form.production_quantity"
                  :label="`${$t('Production Quantity')} *`"
                  box
                  hide-details
                  name="production_quantity"
                  background-color="white"
                  :error="errors.has('Production Quantity')"
                  v-validate="'required|decimal:3|min_value:0'"
                  data-vv-name="Production Quantity"
                />
              </v-flex>

              <v-flex xs3 class="pa-2">
                <v-text-field
                  :value="estimatedQuantity"
                  :label="`${$t('Estimated Product Quantity')} *`"
                  box
                  hide-details
                  readonly
                  name="estimated_product_quantity"
                  background-color="white"
                  :error="errors.has('estimated_product_quantity')"
                  v-validate="'required|decimal:3|min_value:0'"
                  data-vv-name="estimated_product_quantity"
                />
              </v-flex>

              <v-flex xs3 class="pa-2">
                <v-text-field
                  :value="damagedQuantity"
                  :label="`${$t('Damaged Product Quantity')} *`"
                  box
                  hide-details
                  readonly
                  name="damaged_product_quantity"
                  background-color="white"
                  :error="errors.has('damaged_product_quantity')"
                  v-validate="'required|decimal:3|min_value:0'"
                  data-vv-name="damaged_product_quantity"
                />
              </v-flex>

              <v-flex xs6 class="pa-1">
                <v-autocomplete
                  v-model="form.recycle_raw_material_id_fk"
                  :label="`${$t('Recycle Raw Materials')}`"
                  :items="rawMaterialList"
                  hide-details
                  box
                  :data-vv-name="'recycle_raw_material'"
                  background-color="white"
                  :error="errors.has('recycle_raw_material')"
                />
              </v-flex>
              <v-flex xs6 class="pa-1">
                <v-text-field
                  v-model.number="form.recycle_raw_material_quantity"
                  :label="`${$t('Recycle Quantity')}`"
                  hide-details
                  box
                  name="recycle_raw_material_quantity"
                  background-color="white"
                  :error="errors.has('recycle_raw_material_quantity')"
                  v-validate="'decimal:3|min_value:0'"
                />
              </v-flex>

              <v-flex class="pa-2">
                <v-autocomplete
                  :items="shiftTimes"
                  v-model="form.shift_time"
                  :label="`${$t('Shift Time')} *`"
                  box
                  hide-details
                  :error="errors.has('Shift Time')"
                  background-color="white"
                  v-validate="'required'"
                  data-vv-name="Shift Time"
                  :menu-props="{ transition: 'slide-y-transition' }"
                />
              </v-flex>

              <v-flex class="pa-2">
                <v-menu v-model="productionDateMenu" full-width max-width="290" :close-on-content-click="false">
                  <v-text-field
                    slot="activator"
                    :value="form.production_date"
                    :label="`${$t('Production Date')} *`"
                    prepend-inner-icon="event"
                    box
                    readonly
                    hide-details
                    background-color="white"
                    autocomplete="off"
                    data-vv-name="Production Date"
                    :error="errors.has('Production Date')"
                    v-validate="'required|date_format:yyyy-MM-dd'"
                    ref="Production Date"
                  />
                  <v-date-picker @change="productionDateMenu = false" v-model="form.production_date" />
                </v-menu>
              </v-flex>

              <v-flex v-if="settings.product_expiry === 'Active'" xs4 sm4 class="pa-2">
                <v-menu v-model="productionExpireDateMenu" full-width max-width="290" :close-on-content-click="false">
                  <v-text-field
                    slot="activator"
                    :value="form.production_expiration_date"
                    :label="$t('Production Expiration Date')"
                    prepend-inner-icon="event"
                    box
                    readonly
                    hide-details
                    background-color="white"
                    data-vv-name="Production Expiration Date"
                    autocomplete="off"
                    :error="errors.has('Production Expiration Date')"
                    v-validate="'date_format:yyyy-MM-dd|after:Production Date'"
                  />
                  <v-date-picker @change="productionExpireDateMenu = false" v-model="form.production_expiration_date" />
                </v-menu>
              </v-flex>

              <v-flex v-if="showMixtureDetail" xs12 style="margin-top: 40px; margin-bottom:20px">
                <v-layout row wrap justify-center mb-2>
                  <v-flex xs3 ml-5>
                    <h3>{{ $t('Mixture Details') }}</h3>
                  </v-flex>
                  <v-flex xs10 ml-5 mr-5>
                    <v-divider />
                  </v-flex>
                </v-layout>
                <v-layout row wrap class="view-table-header">
                  <v-flex xs1 pa-2>#</v-flex>
                  <v-flex xs5 pa-2>{{ $t('Raw Material') }}</v-flex>
                  <v-flex xs2 pa-2>{{ $t('Quantity') }}</v-flex>
                  <v-flex xs2 pa-2>{{ `${$t('Price')} (${systemCurrency})` }}</v-flex>
                  <v-flex xs2 pa-2>{{ `${$t('Total Cost')} (${systemCurrency})` }}</v-flex>
                </v-layout>

                <template v-if="mixtureInfo.raw_materials.length > 0">
                  <v-layout row wrap v-for="(row, index) in mixtureInfo.raw_materials" :key="index">
                    <v-flex class="view-table-row" xs1>
                      <p class="view-table-content">{{ index + 1 }}</p>
                    </v-flex>
                    <v-flex class="view-table-row" xs5>
                      <p class="view-table-content">
                        {{ row.raw_material_name }}
                      </p>
                    </v-flex>
                    <v-flex class="view-table-row" xs2>
                      <p class="view-table-content">
                        {{ row.mixture_quantity | currency }}
                      </p>
                    </v-flex>
                    <v-flex class="view-table-row" xs2>
                      <p class="view-table-content">
                        {{ row.product_average_price | currency }}
                      </p>
                    </v-flex>
                    <v-flex class="view-table-row" xs2>
                      <p class="view-table-content">
                        {{ row.cost | currency }}
                      </p>
                    </v-flex>
                  </v-layout>
                  <v-layout row wrap>
                    <v-flex xs12 style="text-align: right" pr-4 class="view-table-row">
                      <strong class="view-table-content"
                        >{{ $t('Total') }}: &nbsp; {{ mixtureInfo.mixture_total_cost | currency }}</strong
                      >
                    </v-flex>
                  </v-layout>
                </template>
              </v-flex>
            </v-layout>
          </v-layout>
        </v-card-title>
        <v-divider light />

        <v-card-actions class="grey lighten-3">
          <v-layout column>
            <v-flex xs12 class="pb-2 px-2" style="color: #444;"
              >- {{ $t('Fields marked with asterisk ( * ) are required.') }}</v-flex
            >

            <v-layout justify-end align-center pr-2 pl-2>
              <v-checkbox color="primary" v-model="keepForm" :label="$t('Keep this form to add another one.')" />

              <v-btn outline color="primary" @click="$emit('hidemodal')">{{ $t('Cancel') }}</v-btn>
              <v-btn type="submit" color="success" :loading="btnAddLoading">
                <v-icon :right="$vuetify.rtl" :left="!$vuetify.rtl">save</v-icon>
                {{ $t('Add') }}
              </v-btn>
            </v-layout>
          </v-layout>
        </v-card-actions>
      </form>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import * as helpers from '@/mixins/helperFunctions.js'

export default {
  name: 'add-component',

  props: {
    addDialog: { type: Boolean, required: true }
  },

  data() {
    return {
      btnAddLoading: false,
      form: {},
      showMixtureDetail: false,
      systemCurrency: localStorage.systemCurrency,
      productionDateMenu: false,
      productionExpireDateMenu: false,
      keepForm: false
    }
  },

  computed: {
    ...mapGetters({
      fullStaffList: 'staff/getFullStaffsList',
      fullFactoriesList: 'factories/getFullFactoriesList',
      fullProductList: 'products/getFullProductsList',
      fullMixtureList: 'mixtures/getFullMixturesList',
      shiftTimes: 'productions/getShiftTimes',
      settings: 'configurations/getSystemSettings',
      rawMaterialList: 'rawMaterials/getFullRawMaterialsList',
      mixtureInfo: 'mixtures/getMixtureInfo'
    }),
    estimatedQuantity() {
      const totalRawQuantity = this.mixtureInfo.mixture_total_quantity * this.form.used_mixture_quantity
      const recycleQuantity = this.form.recycle_raw_material_quantity || 0
      const total = (totalRawQuantity - recycleQuantity) / this.mixtureInfo.product_weight
      return isNaN(total) ? 0 : total.toFixed(2)
    },
    damagedQuantity() {
      const total = this.estimatedQuantity - this.form.production_quantity
      return isNaN(total) || total < 0 ? 0 : total.toFixed(2)
    }
  },

  watch: {
    addDialog: {
      immediate: false,
      handler() {
        if (this.addDialog) {
          this.addEscKeyListener()
          this.resetForm()
          this.keepForm = false
        }
      }
    }
  },

  methods: {
    ...mapActions({
      addProduction: 'productions/addProduction',
      fetchMixtureInfo: 'mixtures/fetchMixtureInfo'
    }),

    hasPermissions(permissions) {
      return helpers.userHasPermissions(permissions)
    },

    getMixtureInfo(mixtureId) {
      this.fetchMixtureInfo(mixtureId).then(() => {
        this.showMixtureDetail = true
      })
    },

    addEscKeyListener() {
      document.addEventListener('keyup', e => {
        // if the "ESC" button was pressed and this dialog is shown, hide it
        if (e.keyCode === 27 && this.addDialog) {
          this.$emit('hidemodal')
        }
      })
    },

    resetForm() {
      this.$validator.pause()
      this.showMixtureDetail = false
      for (let key in this.form) {
        this.form[`${key}`] = null
      }
      this.form.production_date = this.$moment()
        .format()
        .substr(0, 10)

      this.$nextTick(() => {
        this.$validator.resume()
        // default veutify autofocus only one time works so we have to handled in this way.
        // document.getElementById('production-focus-me').focus()
      })
    },

    validateBeforeSubmit(event) {
      this.$validator.validateAll().then(() => {
        if (!this.errors.any()) {
          this.btnAddLoading = true
          let formData = new FormData(event.target)
          formData.append('factory_id_fk', this.form.factory_id_fk)
          formData.append('product_id_fk', this.form.product_id_fk)
          formData.append('mixture_id_fk', this.form.mixture_id_fk)
          formData.append('recycle_raw_material_id_fk', this.form.recycle_raw_material_id_fk || '')
          formData.append('shift_time', this.form.shift_time)
          this.form.staff_id_fk.forEach((p, index) => {
            formData.append('staff_id_fk[' + [index] + ']', p)
          })
          formData.append('production_date', this.form.production_date)
          formData.append('production_expiration_date', this.form.production_expiration_date || '')

          this.addProduction(formData)
            .then(() => {
              this.btnAddLoading = false
              this.$emit('reset-datatable')
              if (this.keepForm) {
                this.resetForm()
              } else {
                this.$emit('hidemodal')
              }
            })
            .catch(() => {
              this.btnAddLoading = false
            })
        }
      })
    }
  }
}
</script>

<style scoped />
